import { classifyRoute } from '../routingBoard/classifyRoute';
import type { OpsStatusPipeline } from '../types';
import type { WorkspaceApiLike } from '../routingBoard/types';
import type {
  ComposeCreatePaletteInput,
  CreatePaletteModel,
  CreateSourceStatus,
  IotDeviceLike,
  IotGatewayLike,
  IotListenerLike,
  PaletteSource,
} from './types';

const INVENTED_BLOCKLIST = [
  'pim',
  'dam',
  'product information',
  'digital asset',
  'john deere',
  'jdlink',
  'jd connect',
];

export function matchesSeat(appId: string | null | undefined, scopedAppId?: string | null): boolean {
  if (!scopedAppId) return false;
  return String(appId || '') === scopedAppId;
}

function looksInvented(label: string): boolean {
  const lower = label.toLowerCase();
  return INVENTED_BLOCKLIST.some((term) => lower.includes(term));
}

function statusLabel(status: CreateSourceStatus): string {
  if (status === 'live') return 'Live';
  if (status === 'broken') return 'Broken';
  if (status === 'door_down') return 'Door down';
  if (status === 'unknown') return 'Unknown';
  return 'Quiet';
}

function ingestFromPipeline(pipeline: OpsStatusPipeline, now?: number): PaletteSource | null {
  const apiId = String(pipeline.api_id || '').trim();
  const pipeId = String(pipeline.pipeline_id || '').trim();
  const id = pipeId || apiId;
  if (!id) return null;
  const classified = classifyRoute({
    last_success_at: pipeline.last_success_at,
    last_error: pipeline.last_error,
    health: pipeline.health,
    display_status: pipeline.display_status,
    interval_seconds: pipeline.interval_seconds,
    now,
  });
  const label = pipeline.api_name || apiId || id;
  if (looksInvented(label)) return null;
  return {
    id: `ingest:${id}`,
    class: 'ingest',
    kind: 'ingest_pipe',
    label,
    status: classified.lane,
    statusLabel: statusLabel(classified.lane),
    detail: pipeline.endpoint_id || null,
    last_error: pipeline.last_error ?? null,
    app_id: pipeline.app_id ?? null,
    endpoint_id: pipeline.endpoint_id ?? null,
    store_mode: pipeline.store_mode ?? null,
    census_store: pipeline.census_store ?? null,
  };
}

function ingestFromWorkspace(api: WorkspaceApiLike, now?: number): PaletteSource | null {
  const apiId = String(api.api_id || '').trim();
  if (!apiId) return null;
  const classified = classifyRoute({
    last_success_at: api.runtime?.latest_ingest_at,
    last_error: api.runtime?.last_error,
    health: api.runtime?.lane === 'broken' ? 'error' : api.runtime?.lane === 'live' ? 'healthy' : null,
    interval_seconds: null,
    now,
  });
  const label = api.api_name || apiId;
  if (looksInvented(label)) return null;
  return {
    id: `ingest:${apiId}`,
    class: 'ingest',
    kind: 'ingest_pipe',
    label,
    status: classified.lane,
    statusLabel: statusLabel(classified.lane),
    detail: api.provider || api.status || null,
    last_error: api.runtime?.last_error ?? null,
    app_id: null,
    census_store: api.runtime?.census_store ?? null,
  };
}

export function isFmt100Device(device: IotDeviceLike): boolean {
  const model = String(device.metadata?.model || device.metadata?.device_model || '').toLowerCase();
  const type = String(device.type || '').toLowerCase();
  const imei = String(device.metadata?.imei || device.deviceId || '');
  return model.includes('fmt100') || type.includes('fmt100') || imei === '350317173146469';
}

export function hasMqttListener(listeners: IotListenerLike[] | null | undefined, scopedAppId?: string | null): boolean {
  return (listeners || []).some((listener) => {
    if (scopedAppId && listener.app_id && listener.app_id !== scopedAppId) return false;
    return String(listener.mode || '').toLowerCase() === 'mqtt_bridge';
  });
}

export function tcp5000Listener(
  listeners: IotListenerLike[] | null | undefined,
  scopedAppId?: string | null
): IotListenerLike | null {
  return (listeners || []).find((listener) => {
    if (scopedAppId && listener.app_id && listener.app_id !== scopedAppId) return false;
    const mode = String(listener.mode || '').toLowerCase();
    const port = Number(listener.port);
    return mode === 'tcp' && port === 5000;
  }) || null;
}

/**
 * TCP 5000 is a host bind, not a Kong public door. Never label it live.
 */
export function tcp5000Door(listener: IotListenerLike | null): { door: 'down'; reason: string } {
  if (!listener) {
    return {
      door: 'down',
      reason: 'TCP 5000 is not publicly listening — no tcp listener on this seat',
    };
  }
  const health = String(listener.health?.status || '').toLowerCase();
  if (health && health !== 'online') {
    return {
      door: 'down',
      reason: `TCP 5000 listener ${listener.listenerId || ''} is ${health}, not a public listen door`.trim(),
    };
  }
  return {
    door: 'down',
    reason: 'TCP 5000 is not publicly listening',
  };
}

function iotStatus(
  rawStatus: string | undefined,
  device: IotDeviceLike,
  listeners: IotListenerLike[] | undefined,
  scopedAppId?: string | null
): CreateSourceStatus {
  if (isFmt100Device(device)) {
    const listener = tcp5000Listener(listeners, scopedAppId);
    const door = tcp5000Door(listener);
    if (door.door === 'down') return 'door_down';
  }
  const status = String(rawStatus || '').toLowerCase();
  if (status === 'online') return 'live';
  if (status === 'offline') return 'quiet';
  return 'unknown';
}

function deviceSource(
  device: IotDeviceLike,
  listeners: IotListenerLike[] | undefined,
  scopedAppId?: string | null
): PaletteSource | null {
  const deviceId = String(device.deviceId || device._id || '').trim();
  if (!deviceId) return null;
  const imei = device.metadata?.imei ? String(device.metadata.imei) : null;
  const model = String(device.metadata?.model || device.metadata?.device_model || device.type || 'IoT device');
  const label = imei && imei !== deviceId ? `${model} ${imei}` : `${model} ${deviceId}`;
  if (looksInvented(label)) return null;
  const status = iotStatus(device.status, device, listeners, scopedAppId);
  const listener = isFmt100Device(device) ? tcp5000Listener(listeners, scopedAppId) : null;
  const door = isFmt100Device(device) ? tcp5000Door(listener) : null;
  return {
    id: `iot:device:${deviceId}`,
    class: 'iot',
    kind: 'iot_device',
    label,
    status,
    statusLabel: statusLabel(status),
    detail: door?.reason || device.type || null,
    last_error: door && status === 'door_down' ? door.reason : null,
    app_id: device.app_id ?? null,
    device_id: deviceId,
    imei,
    transport: listener ? 'tcp:5000' : null,
  };
}

function gatewaySource(gateway: IotGatewayLike): PaletteSource | null {
  const gatewayId = String(gateway.gatewayId || gateway._id || '').trim();
  if (!gatewayId) return null;
  const label = [gateway.manufacturer, gateway.model, gateway.type, gatewayId].filter(Boolean).join(' ');
  if (looksInvented(label)) return null;
  const raw = String(gateway.status || '').toLowerCase();
  const status: CreateSourceStatus = raw === 'online' ? 'live' : raw === 'offline' ? 'quiet' : 'unknown';
  return {
    id: `iot:gateway:${gatewayId}`,
    class: 'iot',
    kind: 'iot_gateway',
    label,
    status,
    statusLabel: statusLabel(status),
    detail: gateway.type || null,
    app_id: gateway.app_id ?? null,
    device_id: gatewayId,
    transport: null,
  };
}

/**
 * Palette for the selected app: Ingest pipes + IoT Shack devices/gateways only.
 * No PIM/DAM. No invented MQTT. No invented John Deere/CNH.
 */
export function composeCreatePalette(input: ComposeCreatePaletteInput): CreatePaletteModel {
  const scopedAppId = input.scopedAppId || null;
  if (!scopedAppId) {
    return {
      sources: [],
      ingest: [],
      iot: [],
      emptyReason: 'Select an app to see this seat\'s Ingest pipes and IoT Shack devices.',
    };
  }

  const ingest: PaletteSource[] = [];
  const seen = new Set<string>();
  const seenApiIds = new Set<string>();

  for (const pipeline of input.ops?.api_pipelines?.pipelines || []) {
    if (!matchesSeat(pipeline.app_id, scopedAppId)) continue;
    const source = ingestFromPipeline(pipeline, input.now);
    if (!source || seen.has(source.id)) continue;
    ingest.push(source);
    seen.add(source.id);
    if (pipeline.api_id) seenApiIds.add(String(pipeline.api_id));
  }

  for (const api of input.workspaceApis || []) {
    const apiId = String(api.api_id || '').trim();
    if (apiId && seenApiIds.has(apiId)) continue;
    const source = ingestFromWorkspace(api, input.now);
    if (!source || seen.has(source.id)) continue;
    ingest.push(source);
    seen.add(source.id);
    if (apiId) seenApiIds.add(apiId);
  }

  const iot: PaletteSource[] = [];
  for (const device of input.devices || []) {
    if (device.app_id != null && !matchesSeat(device.app_id, scopedAppId)) continue;
    const source = deviceSource(device, input.listeners || [], scopedAppId);
    if (!source || seen.has(source.id)) continue;
    iot.push(source);
    seen.add(source.id);
  }
  for (const gateway of input.gateways || []) {
    if (gateway.app_id != null && !matchesSeat(gateway.app_id, scopedAppId)) continue;
    const source = gatewaySource(gateway);
    if (!source || seen.has(source.id)) continue;
    iot.push(source);
    seen.add(source.id);
  }

  const sources = [...ingest, ...iot];
  return {
    sources,
    ingest,
    iot,
    emptyReason: sources.length === 0
      ? 'No Ingest pipes or IoT Shack devices on this seat. Palette does not invent sources.'
      : null,
  };
}

export function paletteHasMqtt(listeners: IotListenerLike[] | null | undefined, scopedAppId?: string | null): boolean {
  return hasMqttListener(listeners, scopedAppId);
}
